import { PostHog } from "posthog-js";

import { buildRedeemSessionPayload } from "@/components/pitchCorrection/segmentRender/buildRedeemSessionPayload";
import { expandFrameRangeForSplicing } from "@/components/pitchCorrection/segmentRender/expandFrameRangeForSplicing";
import { findChangedFrameRange } from "@/components/pitchCorrection/segmentRender/findChangedFrameRange";
import {
  CONTOUR_FRAME_HOP_SECONDS,
  CONTOUR_PADDING_FRAMES,
  SEGMENT_CROSSFADE_SECONDS,
  SEGMENT_DURATION_TOLERANCE_SECONDS,
} from "@/constants/pitchCorrection/segmentRender";
import {
  FrameRange,
  InFlightRender,
  SegmentRenderContext,
} from "@/types/defs/pitchCorrection/segmentRender";
import { audioBufferToWav } from "@/utils/audio/audioBufferToWav";
import { decodeAudioArrayBuffer } from "@/utils/audio/decodeAudioArrayBuffer";
import { spliceAudioBuffer } from "@/utils/audio/spliceAudioBuffer";
import { logError } from "@/utils/logError";
import { semitonesToFrequency } from "@/utils/semitonesToFrequency";

export const runner_ =
  ({
    sessionSlug,
    redeemSessionSlug,
    pitchFetchAbortController,
    setIsProcessing,
    setPitchFetchAbortController,
    pitchesAsFrequencyWithPadding,
    pitches,
    setPitchBaselineForPreview,
    artistId,
    signedUrlModel,
    decodedAudioData,
    setDecodedAudioData,
    track,
    posthog,
    pitchFuzzInCents,
    segmentRender,
    inFlightRender,
    queueRunnerRerun,
    isRenderTargetCurrent,
  }: {
    sessionSlug: string | null;
    redeemSessionSlug: string;
    pitchFetchAbortController: AbortController | null;
    setIsProcessing: (ip: boolean) => void;
    setPitchFetchAbortController: (ac: AbortController) => void;
    pitchesAsFrequencyWithPadding: number[];
    pitches: number[];
    setPitchBaselineForPreview: (p: number[]) => void;
    artistId: string | null;
    signedUrlModel: string | null;
    decodedAudioData: string | null;
    setDecodedAudioData: (ab: string) => void;
    track: number;
    posthog: PostHog;
    pitchFuzzInCents: number;
    // null ⇒ full-clip render only (e.g. harmonies), byte-for-byte today's behavior. Non-null ⇒ the editor diffs each edit, asks the backend for just the changed slice, and splices it into the last render client-side.
    segmentRender: SegmentRenderContext | null;
    // Shared across runner invocations so a new pass can see what the in-flight request covers; drives the serialize-or-abort policy at the top of the runner.
    inFlightRender: { current: InFlightRender | null };
    // Schedules another debounced runner pass; fired after an in-flight render settles when a disjoint edit arrived during its flight.
    queueRunnerRerun: () => void;
    // Whether the store this runner's setters write into still describes the file this pass was captured for. The named store survives the editor (deliberately — the post-close flush lands late edits there), but unset() hands it to the NEXT file: a pass whose target is gone must not write, or the closed editor's render becomes the next file's audio and its presence gates the next file's own initial render off.
    isRenderTargetCurrent: () => boolean;
  }) =>
  async () => {
    if (sessionSlug !== null) {
      // The editing target is already gone (unset() ran): the pass has nothing valid to render for, and any commit would corrupt the surviving store's next session. Bail before any side effect.
      if (!isRenderTargetCurrent()) {
        return;
      }
      const headPaddingFrequencies = pitchesAsFrequencyWithPadding.slice(
        0,
        CONTOUR_PADDING_FRAMES
      );
      const tailPaddingFrequencies = pitchesAsFrequencyWithPadding.slice(
        -CONTOUR_PADDING_FRAMES
      );
      const ptrans = [
        ...headPaddingFrequencies,
        ...pitches.map((i) =>
          semitonesToFrequency(i === 0 ? i : i + pitchFuzzInCents / 100)
        ),
        ...tailPaddingFrequencies,
      ];
      // Raw contour values in the same padded coordinates as ptrans, used for silence detection when choosing chunk boundaries. The wire transform can't be used for this: semitonesToFrequency maps silent frames (0) to a nonzero frequency, erasing the 0-means-unvoiced convention.
      const paddedContourValues = [
        ...headPaddingFrequencies,
        ...pitches,
        ...tailPaddingFrequencies,
      ];

      // Serialize-or-abort policy, segment mode only (a harmonies pass can differ by model rather than contour, so it always aborts and replaces). An edit overlapping the in-flight range aborts and replaces the request — a re-drag of the same segment stays as fast as today. An edit to a different segment must NOT abort: the recovery diff would span both segments and degrade to a full render. It waits instead; once the in-flight splice lands, a queued follow-up pass diffs against the advanced baseline and renders exactly the new segment.
      const inFlightRequest =
        segmentRender === null ? null : inFlightRender.current;
      if (inFlightRequest !== null) {
        const editedRangeSinceInFlight = findChangedFrameRange({
          currentFrequencies: ptrans,
          previousFrequencies: inFlightRequest.frequencies,
        });
        if (editedRangeSinceInFlight === null) {
          // The in-flight request is already rendering exactly this contour.
          posthog?.capture("autotunePreviewTriggered");
          return;
        }
        const editOverlapsInFlight =
          inFlightRequest.range === null ||
          (editedRangeSinceInFlight.startIndex <
            inFlightRequest.range.endIndex &&
            inFlightRequest.range.startIndex <
              editedRangeSinceInFlight.endIndex);
        if (!editOverlapsInFlight) {
          inFlightRequest.hasQueuedRerun = true;
          posthog?.capture("autotunePreviewTriggered");
          return;
        }
      }

      // Diff against the last successful render before any side effects. Segment mode only: a harmonies pass (segmentRender === null) can differ by model rather than contour, so it must always render.
      const renderBaselineFrequencies =
        (
          segmentRender === null ||
          segmentRender.renderedAudioBuffer.current === null
        ) ?
          null
        : segmentRender.lastRenderedFrequencies.current;
      const changedRangeSinceLastRender =
        renderBaselineFrequencies === null ? null : (
          findChangedFrameRange({
            currentFrequencies: ptrans,
            previousFrequencies: renderBaselineFrequencies,
          })
        );
      if (
        renderBaselineFrequencies !== null &&
        changedRangeSinceLastRender === null
      ) {
        // The committed render already matches this contour (plain click on the line, undo back onto the rendered state, or a queued rerun whose edit was undone) — rendering would rebuild identical audio and tear down playback for it. A stale in-flight pass would still land older audio, so abort it; the committed baseline is the desired state.
        if (pitchFetchAbortController !== null) {
          pitchFetchAbortController.abort();
        }
        posthog?.capture("autotunePreviewTriggered");
        return;
      }

      setIsProcessing(true);
      if (pitchFetchAbortController !== null) {
        pitchFetchAbortController.abort();
      }

      const controller = new AbortController();
      setPitchFetchAbortController(controller);
      const resolvedSessionSlug = sessionSlug;

      // A newer edit supersedes this invocation by aborting the shared controller, so the in-flight request rejects with AbortError. Any other error (bad status, decode/splice failure, a slice whose decoded length doesn't match the requested range) is a genuine failure that falls back to a full render.
      const wasAborted = (error: unknown): boolean =>
        controller.signal.aborted ||
        (error instanceof DOMException && error.name === "AbortError");

      // Once the response body has been read, abort() can no longer reject this pass, so the commit blocks guard explicitly — a superseded render must never publish its results over a newer one (or leak its blob URL). The target check rides the same gate: unset() mid-flight means the store now belongs to the next file, and the commit must not land there (thrown as an AbortError so the catch below stays silent — there is no user left to toast).
      const throwIfSuperseded = (): void => {
        if (controller.signal.aborted || !isRenderTargetCurrent()) {
          throw new DOMException(
            "Render superseded by a newer edit",
            "AbortError"
          );
        }
      };

      // Null ⇒ a full render is required: no comparable baseline, or the changed chunk grew beyond the ratio worth a targeted render.
      const segmentRange =
        changedRangeSinceLastRender === null ? null : (
          expandFrameRangeForSplicing({
            changedRange: changedRangeSinceLastRender,
            frameValues: paddedContourValues,
            editableFrameCount: pitches.length,
          })
        );

      // Registered before the first request so a later pass can see what this render covers; cleared in the finally below unless a successor that aborted this invocation has already taken the slot.
      const currentRender: InFlightRender = {
        frequencies: ptrans,
        range: segmentRange,
        hasQueuedRerun: false,
      };
      inFlightRender.current = currentRender;

      try {
        const sendRedeemRequest = async (
          range: FrameRange | null
        ): Promise<Response> => {
          // A fresh tag per request keeps every request uniquely tagged (the pre-segment invariant): the fallback full render after a failed segment attempt must not look like a replay of that request to anything the backend keys on the tag.
          const invocationTag = Math.floor(new Date().getTime());
          const payload = buildRedeemSessionPayload({
            frequencies: ptrans,
            slug: resolvedSessionSlug,
            invocationTag,
            artistId,
            signedUrlModel,
            track,
            range,
          });
          const response = await fetch(redeemSessionSlug, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            signal: controller.signal,
          });
          if (!response.ok) {
            throw new Error("Failed to process audio on the server");
          }
          return response;
        };

        const revokePreviousUrl = (): void => {
          if (decodedAudioData !== null) {
            URL.revokeObjectURL(decodedAudioData);
          }
        };

        // Full-audio render via the legacy redeem-session MP3 endpoint. When a segment-render context is present, the decoded MP3 also seeds the splice base for subsequent single-segment edits.
        const runFullRender = async (): Promise<void> => {
          // A full render conflicts with any edit; widening matters when this runs as the fallback after a failed segment attempt.
          currentRender.range = null;
          const response = await sendRedeemRequest(null);
          const arrayBuffer = await response.arrayBuffer();
          // Blob construction copies the bytes, so decoding (which detaches the ArrayBuffer) stays safe. Decode before swapping playback so a decode failure never leaves a played-but-unseeded render.
          const blob = new Blob([arrayBuffer], { type: "audio/mpeg" });
          const decodedBase =
            segmentRender !== null ?
              await decodeAudioArrayBuffer(arrayBuffer)
            : null;
          throwIfSuperseded();
          const url = URL.createObjectURL(blob);
          revokePreviousUrl();
          // The URL stays the editor's gating value and its remount fallback; when a segment context exists, playback itself runs off the decoded buffer so the player never re-fetches the blob.
          setDecodedAudioData(url);
          if (segmentRender !== null && decodedBase !== null) {
            segmentRender.renderedAudioBuffer.current = decodedBase;
            segmentRender.lastRenderedFrequencies.current = ptrans;
            segmentRender.setPlaybackBuffer(decodedBase);
          }
          setIsProcessing(false);
          setPitchBaselineForPreview([...pitches]);
        };

        // Segment render: fetch only the changed range, splice the returned slice into the previous render with crossfades, and hand the spliced buffer straight to playback — no re-encode, no URL churn.
        const runSegmentRender = async (range: FrameRange): Promise<void> => {
          if (
            segmentRender === null ||
            segmentRender.renderedAudioBuffer.current === null
          ) {
            throw new Error("Segment render requires a base buffer");
          }
          const baseBuffer = segmentRender.renderedAudioBuffer.current;
          const response = await sendRedeemRequest(range);

          // Place the returned slice at its frame position using the backend's fixed hop — never a hop inferred from baseBuffer.duration, which decodes short of frame math and drifts toward the clip end.
          const segmentStartSeconds = Math.max(
            0,
            (range.startIndex - CONTOUR_PADDING_FRAMES) *
              CONTOUR_FRAME_HOP_SECONDS
          );
          const expectedSegmentSeconds =
            (range.endIndex - range.startIndex) * CONTOUR_FRAME_HOP_SECONDS;

          const arrayBuffer = await response.arrayBuffer();
          const segmentBuffer = await decodeAudioArrayBuffer(arrayBuffer);
          // A wrong-length slice must throw so the fallback cascade takes over. Splicing it would corrupt silently: a full-clip response (a deployment without segment support answers 200 with one) replaces everything past the splice point with misaligned audio, and even a one-frame-short slice leaves stale audio inside the window that no later diff can repair — lastRenderedFrequencies advances to the new contour regardless.
          const segmentDurationDriftSeconds = Math.abs(
            segmentBuffer.duration - expectedSegmentSeconds
          );
          if (
            segmentDurationDriftSeconds > SEGMENT_DURATION_TOLERANCE_SECONDS
          ) {
            throw new Error(
              `Segment decoded to ${segmentBuffer.duration}s where ` +
                `${expectedSegmentSeconds}s was expected`
            );
          }
          const splicedBuffer = await spliceAudioBuffer({
            baseBuffer,
            segmentBuffer,
            segmentStartSeconds,
            segmentDurationSeconds: expectedSegmentSeconds,
            crossfadeSeconds: SEGMENT_CROSSFADE_SECONDS,
          });
          // The <audio> transport can only play a URL, so the splice is still encoded once for it; the Tone preview player takes the buffer via setPlaybackBuffer and never re-decodes the WAV.
          const wavBlob = await audioBufferToWav({
            audioBuffer: splicedBuffer,
            targetFormat: null,
          });
          throwIfSuperseded();
          const url = URL.createObjectURL(wavBlob);
          revokePreviousUrl();
          setDecodedAudioData(url);
          segmentRender.renderedAudioBuffer.current = splicedBuffer;
          segmentRender.lastRenderedFrequencies.current = ptrans;
          segmentRender.setPlaybackBuffer(splicedBuffer);
          setIsProcessing(false);
          setPitchBaselineForPreview([...pitches]);
        };

        // Fallback cascade: a localized edit tries the segment render first and falls back to a full render; everything else is just a full render. A newer edit aborts the shared controller and unwinds the chain silently.
        const renderAttempts: (() => Promise<void>)[] =
          segmentRange === null ?
            [runFullRender]
          : [() => runSegmentRender(segmentRange), runFullRender];

        for (let attempt = 0; attempt < renderAttempts.length; attempt++) {
          try {
            await renderAttempts[attempt]();
            break;
          } catch (error) {
            const isLastAttempt = attempt === renderAttempts.length - 1;
            if (wasAborted(error) || isLastAttempt) {
              throw error;
            }
            // Surface why the segment attempt was abandoned — otherwise the fallback full render looks like an unexplained duplicate request.
            console.error(
              "Segment render failed; falling back to a full render:",
              error
            );
          }
        }
      } catch (error) {
        if (!wasAborted(error)) {
          // Every render attempt failed — the preview silently keeps playing the previous audio, so the user needs the toast to know why.
          logError(error);
        }
        // This pass's own increment was wiped along with the old file's state when unset() reset the counter, so a decrement here would siphon the NEXT session's processing count (its own render would read as finished while still in flight).
        if (isRenderTargetCurrent()) {
          setIsProcessing(false);
        }
      } finally {
        // A successor that aborted this invocation owns the slot by now; only the owner may clear it and fire the follow-up pass queued by a disjoint edit (a successor's contour already includes that edit).
        if (inFlightRender.current === currentRender) {
          inFlightRender.current = null;
          if (currentRender.hasQueuedRerun) {
            queueRunnerRerun();
          }
        }
      }
    }

    posthog?.capture("autotunePreviewTriggered");
  };
